import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import type { ActivatedRouteSnapshot } from '@angular/router';
import { Event as NavigationEvent, ResolveEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';


@Injectable({
  providedIn: 'root'
})
export class MmuTitleService {
  static readonly routeDataProperty = 'title';

  get title(): Observable<string> {
    return this._title.asObservable();
  }

  applicationName = 'Miele MOVE';
  separator = ' | ';

  private _title: BehaviorSubject<string>;
  private _router$?: Subscription;

  constructor(private _ts: Title, private _router: Router) {
    this._title = new BehaviorSubject<string>('');
    this.title.subscribe((next) => this.setBrowserTitle(next));
    this.reset();
  }

  setBrowserTitle(title: string): void {
    if (title) {
      this._ts.setTitle(title + this.separator + this.applicationName);
    } else {
      this._ts.setTitle(this.applicationName);
    }
  }

  getTitle(): string {
    return this._title.getValue();
  }

  reset(): void {
    this._title.next('');
  }

  subscribeToRouterEvents(): void {
    const routeChange$ = this._router.events.pipe(filter((event: NavigationEvent): event is ResolveEnd => event instanceof ResolveEnd));

    this._router$ = routeChange$.pipe(
      map((event: ResolveEnd) => event.state.root),
      map((route: ActivatedRouteSnapshot) => {
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route;
      }),
      filter((route: ActivatedRouteSnapshot) => route.outlet === 'primary'),
      map((route: ActivatedRouteSnapshot) => {
        while (route.parent && (!route.data || (route.data && !route.data[MmuTitleService.routeDataProperty]))) {
          route = route.parent;
        }
        return route;
      }),
      map((route: ActivatedRouteSnapshot) => route.data)
    ).subscribe((data) => {
      if (Object.prototype.hasOwnProperty.call(data, MmuTitleService.routeDataProperty)) {
        const title = data[MmuTitleService.routeDataProperty];

        if (typeof title === 'string') {
          this._title.next(title);
        } else if (title instanceof Observable) {
          title.pipe(takeUntil(routeChange$)).subscribe((next) => this._title.next(next));
        } else {
          this.reset();
        }
      } else {
        this.reset();
      }
    });
  }
}
